import { FormSelect } from "../../../components/ui/select";
import { FileText } from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { PageTitleRow } from "../../../components/layout/PageTitleRow";
import i18n from "../../../i18n";
import { CRM_ONBOARDING_ADD_PATH } from "../../crm/routes";
import { customerService } from "../../../services/customerService";
import { fetchComplaintDetail, searchComplaintsByStatus } from "../../../services/fsmService";
import {
  createMobileQuotation,
  createQuotation,
  getQuotationById,
  fetchSalesExecutiveOptions,
  fetchSalesSupportOptions,
  mapQuotationTypeForApi,
} from "../../../services/quotationService";
import { useIsMobileLayout } from "../../../hooks/useMediaQuery";
import { QuotationCreateMobileWizard } from "../mobile-pages/QuotationCreateMobileWizard";
import {
  SALES_TYPES,
  sq,
  localizedFieldProps,
  currentIsoDate,
  translateQuotationType,
  normalizeCustomerOption,
  normalizeLocationOption,
  normalizeContactOption,
  fetchEmployeeOptions,
  extractApiCollection,
  extractNumericId,
  extractObjectPayload,
  normalizeApiQuotation,
  ensureSystemWorkingRows,
  hydrateQuotation,
  mapComplaintOption,
  sanitizePrefillText,
  mergeQuotationServiceRoleFields,
  toAssignedEmployeeId,
  resolveEmployeeDisplayName,
  mergeUniqueNamedOptions,
  mergeUniqueComplaintOptions,
  inputClass,
  ADD_ENTITY_OPTION,
} from "./quotationUtils";
import {
  StatusPill,
  SectionShell,
  QuotationPrimaryButton,
  QuotationSecondaryButton,
  ErrorState,
  FormField,
  ComplaintIdInfiniteSelect,
  CustomerInfiniteSelect,
} from "./QuotationComponents";
import { QuotationDetailPage } from "./QuotationDetailPage";


function buildQuotationFromForm(form, options = {}) {
  const fallbackDate = currentIsoDate();
  const systems = Array.isArray(options.systems)
    ? options.systems
        .map((system) => ({
          id: String(system.id || system.systemId || "").trim(),
          name: String(system.name || system.systemName || "").trim(),
        }))
        .filter((system) => system.name)
    : [];
  return hydrateQuotation({
    id: options.id ?? null,
    refId: form.refId.trim().toUpperCase(),
    version: 1,
    customer: form.customer,
    customerId: options.customerId ?? null,
    location: form.location,
    locationId: options.locationId ?? null,
    pointOfContact: form.contactName,
    phone: form.phone,
    email: form.email,
    salesExecutive: options.salesExecutiveName || form.salesExecutive || "",
    salesSupport: options.salesSupportName || form.salesSupport || "",
    assignedSalesExecutive: form.assignedSalesExecutive || "",
    assignedSalesSupport: form.assignedSalesSupport || "",
    technicianId: form.technicianId || "",
    managerId: form.managerId || "",
    technicianName: form.technicianName || "",
    managerName: form.managerName || "",
    productServiceIds: Array.isArray(options.productServiceIds) ? options.productServiceIds : [],
    systemNames: systems.map((system) => system.name),
    systemName: systems.map((system) => system.name).join(", "),
    systems,
    type: form.type,
    complaintId: form.type === "SERVICE" ? form.complaintId || "" : "",
    status: "Draft",
    sellingPrice: "Rs0",
    tenderDue: form.expiry || fallbackDate,
    expiry: form.expiry,
    updated: fallbackDate,
    workingRows: ensureSystemWorkingRows([], systems, form.type),
    additionalTerms: "",
  });
}

/** Map backend validation field names → Create Quotation form keys. */
const CREATE_QUOTATION_FIELD_MAP = {
  refId: "refId",
  ref_id: "refId",
  type: "type",
  quotationType: "type",
  quotation_type: "type",
  complaintId: "complaintId",
  complaint_id: "complaintId",
  expiryDate: "expiry",
  expiry: "expiry",
  quotationExpiryDate: "expiry",
  tenderDueDate: "tenderDueDate",
  tender_due_date: "tenderDueDate",
  customerId: "customer",
  customer_id: "customer",
  customerName: "customer",
  customer: "customer",
  locationId: "location",
  location_id: "location",
  locationName: "location",
  address: "location",
  location: "location",
  contactName: "contactName",
  pointOfContactName: "contactName",
  contactPhone: "phone",
  phone: "phone",
  contactEmail: "email",
  email: "email",
  assignedSalesExecutive: "assignedSalesExecutive",
  assignedSalesSupport: "assignedSalesSupport",
  technicianId: "technicianId",
  managerId: "managerId",
  excelFile: "excelFile",
  excel_file: "excelFile",
};

function resolveCreateQuotationFormField(field) {
  const raw = String(field || "")
    .trim()
    .replace(/^\[?'?(.+?)'?]$/, "$1")
    .split(".")
    .pop();
  if (!raw) return "";
  if (CREATE_QUOTATION_FIELD_MAP[raw]) return CREATE_QUOTATION_FIELD_MAP[raw];
  const camel = raw.replace(/_([a-z])/g, (_, char) => char.toUpperCase());
  return CREATE_QUOTATION_FIELD_MAP[camel] || "";
}

function readCreateQuotationErrorText(value) {
  if (value == null) return "";
  if (typeof value === "string") return value.trim();
  if (Array.isArray(value)) {
    return value.map((item) => readCreateQuotationErrorText(item)).filter(Boolean).join(", ");
  }
  if (typeof value === "object") {
    return readCreateQuotationErrorText(
      value.message ?? value.defaultMessage ?? value.error ?? value.reason ?? ""
    );
  }
  return String(value).trim();
}

function collectCreateQuotationFieldErrors(payload) {
  const errors = {};
  if (!payload || typeof payload !== "object") return errors;

  const assign = (field, message) => {
    const formKey = resolveCreateQuotationFormField(field);
    const text = readCreateQuotationErrorText(message);
    if (!formKey || !text) return;
    errors[formKey] = text;
  };

  const objectCollections = [
    payload.fieldErrors,
    payload.validationErrors,
    payload.errors,
    payload.data?.fieldErrors,
    payload.data?.validationErrors,
    payload.data?.errors,
  ];
  for (const collection of objectCollections) {
    if (!collection || Array.isArray(collection) || typeof collection !== "object") continue;
    Object.entries(collection).forEach(([field, message]) => {
      if (String(field).toLowerCase() === "message") return;
      assign(field, message);
    });
  }

  if (payload.data && typeof payload.data === "object" && !Array.isArray(payload.data)) {
    const skipKeys = new Set([
      "message", "error", "status", "code", "success", "timestamp", "id", "quotationId",
    ]);
    Object.entries(payload.data).forEach(([field, message]) => {
      if (skipKeys.has(field)) return;
      if (typeof message === "string" || Array.isArray(message)) assign(field, message);
    });
  }

  const arrayCollections = [
    payload.details,
    payload.errors,
    payload.validationErrors,
    payload.fieldErrors,
    payload.data?.details,
    payload.data?.errors,
  ];
  for (const collection of arrayCollections) {
    if (!Array.isArray(collection)) continue;
    collection.forEach((item) => {
      if (!item || typeof item !== "object") return;
      assign(
        item.field ?? item.name ?? item.property ?? item.path ?? item.fieldName,
        item.message ?? item.defaultMessage ?? item.error ?? item.reason
      );
    });
  }

  return errors;
}

/** Exact backend message + mapped field errors for Create Quotation (no generic replacements). */
function extractCreateQuotationApiError(error) {
  const payload = error?.payload ?? (error?.data && typeof error.data === "object" ? { data: error.data } : null);
  const messageCandidates = [
    error?.message,
    payload?.message,
    typeof payload?.data === "string" ? payload.data : "",
    payload?.data?.message,
    payload?.data?.error,
    typeof error?.data === "string" ? error.data : "",
    error?.data?.message,
    error?.data?.error,
    payload?.error,
  ];
  const message = messageCandidates
    .map((value) => readCreateQuotationErrorText(value))
    .find(Boolean) || "";
  const fieldErrors = collectCreateQuotationFieldErrors(payload || error?.data || {});
  return { message, fieldErrors };
}

function CreateQuotationForm() {
  useTranslation();
  const navigate = useNavigate();
  const isMobileLayout = useIsMobileLayout();
  const [mobileStep, setMobileStep] = useState(0);
  const [form, setForm] = useState({
    refId: "",
    type: "",
    complaintId: "",
    assignedSalesExecutive: "",
    assignedSalesSupport: "",
    salesExecutive: "",
    salesSupport: "",
    technicianId: "",
    managerId: "",
    technicianName: "",
    managerName: "",
    customer: "",
    location: "",
    contactName: "",
    phone: "",
    email: "",
    tenderDueDate: "",
    expiry: "",
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const excelFileInputRef = useRef(null);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [contactOptions, setContactOptions] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [salesExecutiveOptions, setSalesExecutiveOptions] = useState([]);
  const [salesSupportOptions, setSalesSupportOptions] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [loadingSalesExecutives, setLoadingSalesExecutives] = useState(false);
  const [loadingSalesSupport, setLoadingSalesSupport] = useState(false);
  const employeesRequestedRef = useRef(false);
  const salesExecutivesRequestedRef = useRef(false);
  const salesSupportRequestedRef = useRef(false);
  const [complaintOptions, setComplaintOptions] = useState([]);
  const [complaintPage, setComplaintPage] = useState(0);
  const [complaintHasMore, setComplaintHasMore] = useState(false);
  const [loadingComplaints, setLoadingComplaints] = useState(false);
  const [loadingMoreComplaints, setLoadingMoreComplaints] = useState(false);
  const [loadingComplaintDetail, setLoadingComplaintDetail] = useState(false);

  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [loadingMoreCustomers, setLoadingMoreCustomers] = useState(false);
  const [customerPage, setCustomerPage] = useState(0);
  const [customerHasMore, setCustomerHasMore] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);

  const [customerApiError, setCustomerApiError] = useState("");
  const [employeeApiError, setEmployeeApiError] = useState("");
  const [complaintApiError, setComplaintApiError] = useState("");
  const [complaintDetailError, setComplaintDetailError] = useState("");
  const loadingMoreComplaintsRef = useRef(false);
  const complaintPageRef = useRef(0);
  const complaintHasMoreRef = useRef(false);
  const complaintPrefillRef = useRef(null);
  const loadingMoreCustomersRef = useRef(false);
  const customerPageRef = useRef(0);
  const customerHasMoreRef = useRef(false);

  const selectedCustomer = customerOptions.find((item) => item.name === form.customer) || null;
  const selectedLocation = locationOptions.find((item) => item.name === form.location) || null;
  const isServiceType = form.type === "SERVICE";
  const showServiceRoles = isServiceType && Boolean(String(form.complaintId || "").trim());

  const serviceExecutiveLabel = useMemo(
    () => resolveEmployeeDisplayName(form.technicianId, employeeOptions, form.technicianName),
    [form.technicianId, form.technicianName, employeeOptions]
  );
  const serviceManagerLabel = useMemo(
    () => resolveEmployeeDisplayName(form.managerId, employeeOptions, form.managerName),
    [form.managerId, form.managerName, employeeOptions]
  );

  function patchForm(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: "" }));
    setSubmitError("");
  }

  function handleQuotationTypeChange(nextType) {
    setForm((current) => ({
      ...current,
      type: nextType,
      complaintId: nextType === "SERVICE" ? current.complaintId : "",
      technicianId: nextType === "SERVICE" ? current.technicianId : "",
      managerId: nextType === "SERVICE" ? current.managerId : "",
      technicianName: nextType === "SERVICE" ? current.technicianName : "",
      managerName: nextType === "SERVICE" ? current.managerName : "",
    }));
    setErrors((current) => ({ ...current, type: "", complaintId: "" }));
    setSubmitError("");
    if (nextType !== "SERVICE") {
      setComplaintOptions([]);
      setComplaintPage(0);
      complaintPageRef.current = 0;
      setComplaintHasMore(false);
      complaintHasMoreRef.current = false;
      setLoadingMoreComplaints(false);
      loadingMoreComplaintsRef.current = false;
      setComplaintApiError("");
      setComplaintDetailError("");
      setLoadingComplaintDetail(false);
      complaintPrefillRef.current = null;
    }
  }

  function clearComplaintPrefillFields() {
    complaintPrefillRef.current = null;
    setComplaintDetailError("");
    setForm((current) => ({
      ...current,
      customer: "",
      location: "",
      contactName: "",
      phone: "",
      email: "",
      technicianId: "",
      managerId: "",
      technicianName: "",
      managerName: "",
    }));
    setErrors((current) => ({
      ...current,
      customer: "",
      location: "",
      contactName: "",
    }));
  }

  function applyComplaintDetailPrefill(detail, complaintId) {
    const raw = detail?.raw && typeof detail.raw === "object" ? detail.raw : {};
    const customerName = sanitizePrefillText(
      detail?.customerName ??
      raw.companyName ??
      raw.customerName ??
      raw.customer?.companyName ??
      raw.customer?.customerName
    );
    const customerId =
      detail?.customerId ??
      raw.customerId ??
      raw.customer?.customerId ??
      null;
    const locationName = sanitizePrefillText(
      detail?.locationName ??
      raw.locationName ??
      raw.address ??
      raw.customerLocation ??
      raw.location?.locationName ??
      raw.location?.address
    );
    const locationId =
      detail?.locationId ??
      raw.locationId ??
      raw.location?.locationId ??
      null;
    const contactName = sanitizePrefillText(
      detail?.pointOfContact ??
      raw.pointOfContact ??
      raw.contactName
    );
    const phone = sanitizePrefillText(
      detail?.contactNumber ??
      raw.contactNumber ??
      raw.phone ??
      raw.mobile
    );
    const email = sanitizePrefillText(detail?.email ?? raw.email);
    const technicianId =
      detail?.technicianId ??
      raw.technicianId ??
      raw.technician?.empId ??
      raw.technician?.technicianId ??
      "";
    const managerId =
      detail?.managerId ??
      raw.managerId ??
      raw.manager?.empId ??
      raw.manager?.managerId ??
      "";
    const technicianName = sanitizePrefillText(
      detail?.technicianName ??
      raw.technicianName ??
      raw.technician?.name ??
      raw.technician?.employeeName
    );
    const managerName = sanitizePrefillText(
      detail?.managerName ??
      raw.managerName ??
      raw.manager?.name ??
      raw.manager?.employeeName
    );

    const serviceRoles = mergeQuotationServiceRoleFields(
      {
        technicianId,
        managerId,
        technicianName,
        managerName,
      },
      {
        ...raw,
        ...(detail && typeof detail === "object" ? detail : {}),
      }
    );

    const prefill = {
      complaintId: String(complaintId),
      customerId,
      customerName,
      locationId,
      locationName,
      contactName,
      phone,
      email,
      technicianId: serviceRoles.technicianId,
      managerId: serviceRoles.managerId,
      technicianName: serviceRoles.technicianName,
      managerName: serviceRoles.managerName,
    };
    complaintPrefillRef.current = prefill;

    let resolvedCustomerName = customerName;
    if (customerName) {
      setCustomerOptions((current) => {
        const existing = current.find(
          (item) =>
            (customerId != null && customerId !== "" && String(item.id) === String(customerId)) ||
            String(item.name || "").trim().toLowerCase() === customerName.toLowerCase()
        );
        if (existing) {
          resolvedCustomerName = existing.name;
          return current;
        }
        if (customerId == null || customerId === "") return current;
        return mergeUniqueNamedOptions(current, {
          id: customerId,
          name: customerName,
        });
      });
    }

    let resolvedLocationName = locationName;
    if (locationName && locationId != null && locationId !== "") {
      setLocationOptions((current) => {
        const existing = current.find(
          (item) =>
            String(item.id) === String(locationId) ||
            String(item.name || "").trim().toLowerCase() === locationName.toLowerCase()
        );
        if (existing) {
          resolvedLocationName = existing.name;
          return mergeUniqueNamedOptions(current, {
            ...existing,
            id: existing.id || locationId,
            name: existing.name || locationName,
            customerId: existing.customerId || customerId || "",
          });
        }
        return mergeUniqueNamedOptions(current, {
          id: locationId,
          name: locationName,
          customerId: customerId || "",
        });
      });
    }

    let resolvedContactName = contactName;
    if (contactName) {
      setContactOptions((current) => {
        const existing = current.find(
          (item) =>
            String(item.name || "").trim().toLowerCase() === contactName.toLowerCase()
        );
        if (existing) {
          resolvedContactName = existing.name;
          return mergeUniqueNamedOptions(current, {
            ...existing,
            phone: phone || existing.phone || "",
            email: email || existing.email || "",
          });
        }
        return mergeUniqueNamedOptions(current, {
          id: raw.contactId ?? raw.contact?.contactId ?? `complaint-contact-${complaintId}`,
          name: contactName,
          phone,
          email,
        });
      });
    }

    setForm((current) => ({
      ...current,
      customer: resolvedCustomerName,
      location: resolvedLocationName,
      contactName: resolvedContactName,
      phone,
      email,
      technicianId: serviceRoles.technicianId,
      managerId: serviceRoles.managerId,
      technicianName: serviceRoles.technicianName,
      managerName: serviceRoles.managerName,
      assignedSalesExecutive: "",
      assignedSalesSupport: "",
      salesExecutive: "",
      salesSupport: "",
    }));
    setErrors((current) => ({
      ...current,
      customer: "",
      location: "",
      contactName: "",
      complaintId: "",
    }));

    if (!customerName && !locationName && !contactName && !phone && !email) {
      setComplaintDetailError("Complaint details were empty. Please fill customer details manually.");
    }
  }

  function handleComplaintIdChange(nextValue) {
    const complaintId = String(nextValue || "").trim();
    patchForm("complaintId", complaintId);
    if (!complaintId) {
      clearComplaintPrefillFields();
    }
  }

  async function fetchComplaintPage(page, { append = false } = {}) {
    const response = await searchComplaintsByStatus("In_Progress", {
      page,
      size: 10,
      sortBy: "complaintId",
      sortDir: "DESC",
    });
    const mapped = (Array.isArray(response?.items) ? response.items : [])
      .map(mapComplaintOption)
      .filter(Boolean);

    setComplaintOptions((current) => (append ? mergeUniqueComplaintOptions(current, mapped) : mergeUniqueComplaintOptions([], mapped)));
    setComplaintPage(page);
    complaintPageRef.current = page;
    const hasMore =
      mapped.length === 0
        ? false
        : typeof response?.hasMore === "boolean"
          ? response.hasMore
          : mapped.length >= 10;
    setComplaintHasMore(hasMore);
    complaintHasMoreRef.current = hasMore;
    return response;
  }

  async function loadMoreComplaints() {
    if (
      !isServiceType ||
      !complaintHasMoreRef.current ||
      loadingComplaints ||
      loadingMoreComplaintsRef.current
    ) {
      return;
    }

    loadingMoreComplaintsRef.current = true;
    setLoadingMoreComplaints(true);
    setComplaintApiError("");

    try {
      await fetchComplaintPage(complaintPageRef.current + 1, { append: true });
    } catch (error) {
      setComplaintApiError(error.message || "Unable to load more complaint IDs.");
    } finally {
      loadingMoreComplaintsRef.current = false;
      setLoadingMoreComplaints(false);
    }
  }

  async function fetchCustomerPage(page, { append = false } = {}) {
    const response = await customerService.getCustomers(page, 10, "customerId", "ASC");
    const mapped = (Array.isArray(response?.customers) ? response.customers : [])
      .map(normalizeCustomerOption)
      .filter((item) => item.id && item.name);

    setCustomerOptions((current) => {
      if (!append) return mapped;
      const seen = new Set(current.map((item) => String(item.id)));
      const next = [...current];
      mapped.forEach((item) => {
        if (seen.has(String(item.id))) return;
        seen.add(String(item.id));
        next.push(item);
      });
      return next;
    });
    setCustomerPage(page);
    customerPageRef.current = page;

    const totalPages = Number(response?.totalPages);
    const hasMore =
      Number.isFinite(totalPages) && totalPages > 0
        ? page + 1 < totalPages
        : mapped.length >= 10;
    setCustomerHasMore(hasMore);
    customerHasMoreRef.current = hasMore;
    return response;
  }

  async function loadMoreCustomers() {
    if (!customerHasMoreRef.current || loadingCustomers || loadingMoreCustomersRef.current) {
      return;
    }

    loadingMoreCustomersRef.current = true;
    setLoadingMoreCustomers(true);
    setCustomerApiError("");

    try {
      await fetchCustomerPage(customerPageRef.current + 1, { append: true });
    } catch (error) {
      setCustomerApiError(error.message || "Unable to load more customers.");
    } finally {
      loadingMoreCustomersRef.current = false;
      setLoadingMoreCustomers(false);
    }
  }

  // 1. Fetch Customers: GET /saas/crm/customer-info/search (page 0 first, then infinite scroll)
  useEffect(() => {
    let active = true;

    (async () => {
      setLoadingCustomers(true);
      setCustomerApiError("");
      setCustomerOptions([]);
      setCustomerPage(0);
      customerPageRef.current = 0;
      setCustomerHasMore(false);
      customerHasMoreRef.current = false;
      try {
        await fetchCustomerPage(0);
      } catch (error) {
        if (!active) return;
        setCustomerOptions([]);
        setCustomerHasMore(false);
        customerHasMoreRef.current = false;
        setCustomerApiError(error.message || "Unable to load CRM customer details.");
      } finally {
        if (active) setLoadingCustomers(false);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  async function ensureEmployeeOptions() {
    if (employeesRequestedRef.current) return;
    employeesRequestedRef.current = true;
    setLoadingEmployees(true);
    setEmployeeApiError("");
    try {
      const employees = await fetchEmployeeOptions();
      setEmployeeOptions(employees);
    } catch (error) {
      employeesRequestedRef.current = false;
      setEmployeeOptions([]);
      setEmployeeApiError(error.message || "Unable to load employee options.");
    } finally {
      setLoadingEmployees(false);
    }
  }

  async function ensureSalesExecutiveOptions() {
    if (salesExecutivesRequestedRef.current) return;
    salesExecutivesRequestedRef.current = true;
    setLoadingSalesExecutives(true);
    setEmployeeApiError("");
    try {
      const options = await fetchSalesExecutiveOptions();
      setSalesExecutiveOptions(options);
    } catch (error) {
      salesExecutivesRequestedRef.current = false;
      setSalesExecutiveOptions([]);
      setEmployeeApiError(error.message || "Unable to load sales executive options.");
    } finally {
      setLoadingSalesExecutives(false);
    }
  }

  async function ensureSalesSupportOptions() {
    if (salesSupportRequestedRef.current) return;
    salesSupportRequestedRef.current = true;
    setLoadingSalesSupport(true);
    setEmployeeApiError("");
    try {
      const options = await fetchSalesSupportOptions();
      setSalesSupportOptions(options);
    } catch (error) {
      salesSupportRequestedRef.current = false;
      setSalesSupportOptions([]);
      setEmployeeApiError(error.message || "Unable to load sales support options.");
    } finally {
      setLoadingSalesSupport(false);
    }
  }

  // Fetch Complaint IDs when Quotation Type is SERVICE (page 0 first, then infinite scroll)
  useEffect(() => {
    let active = true;

    if (!isServiceType) {
      setComplaintOptions([]);
      setComplaintPage(0);
      complaintPageRef.current = 0;
      setComplaintHasMore(false);
      complaintHasMoreRef.current = false;
      setLoadingComplaints(false);
      setLoadingMoreComplaints(false);
      loadingMoreComplaintsRef.current = false;
      setComplaintApiError("");
      return () => {
        active = false;
      };
    }

    (async () => {
      setLoadingComplaints(true);
      setComplaintApiError("");
      setComplaintOptions([]);
      setComplaintPage(0);
      complaintPageRef.current = 0;
      setComplaintHasMore(false);
      complaintHasMoreRef.current = false;
      try {
        await fetchComplaintPage(0);
      } catch (error) {
        if (!active) return;
        setComplaintOptions([]);
        setComplaintHasMore(false);
        complaintHasMoreRef.current = false;
        setComplaintApiError(error.message || "Unable to load complaint IDs.");
      } finally {
        if (active) setLoadingComplaints(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [isServiceType]);

  // When SERVICE + valid Complaint ID: GET /saas/fsm/customer-complaints/{complaintId}
  // and auto-populate customer / location / contact fields.
  useEffect(() => {
    let active = true;

    if (!isServiceType) {
      setLoadingComplaintDetail(false);
      setComplaintDetailError("");
      complaintPrefillRef.current = null;
      return () => {
        active = false;
      };
    }

    const complaintId = String(form.complaintId || "").trim();
    if (!complaintId) {
      setLoadingComplaintDetail(false);
      setComplaintDetailError("");
      complaintPrefillRef.current = null;
      return () => {
        active = false;
      };
    }

    (async () => {
      setLoadingComplaintDetail(true);
      setComplaintDetailError("");
      try {
        const detail = await fetchComplaintDetail(complaintId);
        if (!active) return;
        if (!detail) {
          clearComplaintPrefillFields();
          setComplaintDetailError("No complaint details found for the selected Complaint ID.");
          return;
        }
        applyComplaintDetailPrefill(detail, complaintId);
      } catch (error) {
        if (!active) return;
        clearComplaintPrefillFields();
        setForm((current) => ({ ...current, complaintId }));
        setComplaintDetailError(error.message || "Unable to load complaint details.");
      } finally {
        if (active) setLoadingComplaintDetail(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [isServiceType, form.complaintId]);

  // 2 & 3. Fetch Locations and Contacts after customer selection
  // GET /saas/crm/customer-locations/search/customer?customerId={customerId}&page=0&size=10&sortBy=locationId&sortDir=ASC
  // GET /saas/crm/customer-contact/search/customer?customerId={customerId}&page=0&size=10&sortBy=contactId&sortDir=ASC
  useEffect(() => {
    let active = true;

    if (!selectedCustomer?.id) {
      setLocationOptions([]);
      setContactOptions([]);
      return;
    }

    const customerId = selectedCustomer.id;
    const prefill = complaintPrefillRef.current;

    setLoadingLocations(true);
    customerService
      .getLocationsByCustomer(customerId, 0, 10, "locationId", "ASC")
      .then((response) => {
        if (!active) return;
        let locations = Array.isArray(response?.locations)
          ? response.locations.map(normalizeLocationOption).filter((item) => item.id && item.name)
          : [];
        if (
          prefill?.locationName &&
          prefill.locationId != null &&
          prefill.locationId !== "" &&
          String(prefill.customerId || "") === String(customerId) &&
          String(prefill.complaintId || "") === String(form.complaintId || "").trim()
        ) {
          locations = mergeUniqueNamedOptions(locations, {
            id: prefill.locationId,
            name: prefill.locationName,
            customerId,
          });
        } else if (
          prefill?.locationName &&
          String(prefill.customerId || "") === String(customerId) &&
          String(prefill.complaintId || "") === String(form.complaintId || "").trim()
        ) {
          const matched = locations.find(
            (item) =>
              String(item.name || "").trim().toLowerCase() ===
              String(prefill.locationName).trim().toLowerCase()
          );
          if (matched) {
            setForm((current) =>
              current.location === prefill.locationName || !current.location
                ? { ...current, location: matched.name }
                : current
            );
          }
        }
        setLocationOptions(locations);
      })
      .catch((error) => {
        if (!active) return;
        const fallback = [];
        if (
          prefill?.locationName &&
          prefill.locationId != null &&
          prefill.locationId !== "" &&
          String(prefill.customerId || "") === String(customerId) &&
          String(prefill.complaintId || "") === String(form.complaintId || "").trim()
        ) {
          fallback.push({
            id: prefill.locationId,
            name: prefill.locationName,
            customerId,
          });
        }
        setLocationOptions(fallback);
        console.warn("Failed to load customer locations:", error);
      })
      .finally(() => {
        if (active) setLoadingLocations(false);
      });

    setLoadingContacts(true);
    customerService
      .getContactsByCustomer(customerId, 0, 10, "contactId", "ASC")
      .then((response) => {
        if (!active) return;
        let contacts = Array.isArray(response?.contacts)
          ? response.contacts.map(normalizeContactOption).filter((item) => item.id && item.name)
          : [];
        if (
          prefill?.contactName &&
          String(prefill.customerId || "") === String(customerId) &&
          String(prefill.complaintId || "") === String(form.complaintId || "").trim()
        ) {
          contacts = mergeUniqueNamedOptions(contacts, {
            id: `complaint-contact-${prefill.complaintId}`,
            name: prefill.contactName,
            phone: prefill.phone || "",
            email: prefill.email || "",
          });
        }
        setContactOptions(contacts);
      })
      .catch((error) => {
        if (!active) return;
        const fallback = [];
        if (
          prefill?.contactName &&
          String(prefill.customerId || "") === String(customerId) &&
          String(prefill.complaintId || "") === String(form.complaintId || "").trim()
        ) {
          fallback.push({
            id: `complaint-contact-${prefill.complaintId}`,
            name: prefill.contactName,
            phone: prefill.phone || "",
            email: prefill.email || "",
          });
        }
        setContactOptions(fallback);
        console.warn("Failed to load customer contacts:", error);
      })
      .finally(() => {
        if (active) setLoadingContacts(false);
      });

    return () => {
      active = false;
    };
  }, [selectedCustomer?.id, form.complaintId]);

  function handleCustomerChange(customerName) {
    if (customerName === ADD_ENTITY_OPTION) {
      navigate(CRM_ONBOARDING_ADD_PATH);
      return;
    }
    complaintPrefillRef.current = null;
    patchForm("customer", customerName);
    setForm((current) => ({
      ...current,
      customer: customerName,
      location: "",
      contactName: "",
      phone: "",
      email: "",
    }));
    setErrors((current) => ({
      ...current,
      customer: "",
      location: "",
      contactName: "",
    }));
  }

  function handleLocationChange(event) {
    const locationName = event.target.value;
    if (locationName === ADD_ENTITY_OPTION) {
      navigate(CRM_ONBOARDING_ADD_PATH);
      return;
    }
    patchForm("location", locationName);
  }

  function handleContactChange(event) {
    const contactName = event.target.value;
    if (contactName === ADD_ENTITY_OPTION) {
      navigate(CRM_ONBOARDING_ADD_PATH);
      return;
    }
    patchForm("contactName", contactName);
    const foundContact = contactOptions.find((item) => item.name === contactName);
    if (foundContact) {
      setForm((current) => ({
        ...current,
        contactName,
        phone: foundContact.phone || "",
        email: foundContact.email || "",
      }));
    } else {
      setForm((current) => ({
        ...current,
        contactName,
        phone: "",
        email: "",
      }));
    }
  }

  function validate() {
    const next = {};
    if (!form.refId.trim()) next.refId = "Ref ID is required";
    if (!form.type) next.type = "Quotation type is required";
    if (form.type === "SERVICE" && !String(form.complaintId || "").trim()) {
      next.complaintId = sq("complaintIdRequired");
    }
    if (!form.customer) next.customer = "Customer is required";
    if (!form.location) next.location = "Address or location is required";
    if (!form.expiry) next.expiry = "Quotation expiry date is required";
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleCreate() {
    if (!validate()) return;
    if (!selectedCustomer || !selectedLocation) {
      setSubmitError("Please choose a valid customer and saved location.");
      return;
    }

    const assignedSalesExecutive = toAssignedEmployeeId(form.assignedSalesExecutive);
    const assignedSalesSupport = toAssignedEmployeeId(form.assignedSalesSupport);
    const salesExecutiveName = resolveEmployeeDisplayName(
      form.assignedSalesExecutive,
      salesExecutiveOptions,
      form.salesExecutive
    );
    const salesSupportName = resolveEmployeeDisplayName(
      form.assignedSalesSupport,
      salesSupportOptions,
      form.salesSupport
    );

    const draftQuotation = buildQuotationFromForm(form, {
      customerId: selectedCustomer.id,
      locationId: selectedLocation.id,
      systems: [],
      salesExecutiveName,
      salesSupportName,
    });

    setSubmitting(true);
    setSubmitError("");

    try {

      // Shared field mapping for desktop /add and mobile /mobile-add/.
      const fieldsPayload = {
        refId: form.refId.trim().toUpperCase(),
        type: mapQuotationTypeForApi(form.type),
        customerId: selectedCustomer.id,
        customerName: selectedCustomer.name,
        locationId: selectedLocation.id,
        locationName: selectedLocation.name,
        address: selectedLocation.name,
        contactName: form.contactName.trim(),
        pointOfContactName: form.contactName.trim(),
        contactPhone: form.phone.trim(),
        contactEmail: form.email.trim(),
        tenderDueDate: form.tenderDueDate || form.expiry,
        expiryDate: form.expiry,
        ...(showServiceRoles
          ? {
              technicianId: Number(form.technicianId) || form.technicianId || undefined,
              managerId: Number(form.managerId) || form.managerId || undefined,
              serviceExecutive: serviceExecutiveLabel || undefined,
              serviceManager: serviceManagerLabel || undefined,
            }
          : {
              assignedSalesExecutive,
              assignedSalesSupport,
            }),
        ...(form.type === "SERVICE"
          ? { complaintId: Number(form.complaintId) || form.complaintId }
          : {}),
      };

      // Mobile-only endpoint; desktop keeps multipart /quotations/add (+ optional Excel).
      const response = isMobileLayout
        ? await createMobileQuotation(fieldsPayload)
        : await createQuotation({
            ...fieldsPayload,
            ...(excelFile ? { excelFile } : {}),
          });
      const createPayload =
        extractObjectPayload(response?.data) ||
        extractObjectPayload(response) ||
        null;
      const createdId =
        extractNumericId(createPayload?.id ?? createPayload?.quotationId) ||
        extractNumericId(response?.data?.id) ||
        extractNumericId(response?.id);

      // Reload detail so the workspace hydrates from GET (mobile always; desktop when Excel import).
      let detailPayload = createPayload;
      if (createdId && (isMobileLayout || excelFile)) {
        try {
          const latestResponse = await getQuotationById(createdId);
          const latestPayload =
            extractObjectPayload(latestResponse?.data) ||
            extractObjectPayload(latestResponse) ||
            null;
          const latestLineItems = extractApiCollection(latestPayload?.lineItems);
          const createLineItems = extractApiCollection(createPayload?.lineItems);
          if (latestPayload && (latestLineItems.length > 0 || createLineItems.length === 0 || isMobileLayout)) {
            detailPayload = {
              ...createPayload,
              ...latestPayload,
              lineItems: latestLineItems.length > 0 ? latestLineItems : createLineItems,
            };
          }
        } catch {
          // Keep create response when detail reload fails.
        }
      }

      const createdQuotation = detailPayload
        ? normalizeApiQuotation({
            ...detailPayload,
            customerName: detailPayload.customerName || draftQuotation.customer,
            locationName: detailPayload.locationName || draftQuotation.location,
            salesExecutive: salesExecutiveName || detailPayload.salesExecutive,
            salesSupport: salesSupportName || detailPayload.salesSupport,
            assignedSalesExecutive:
              assignedSalesExecutive || detailPayload.assignedSalesExecutive,
            assignedSalesSupport:
              assignedSalesSupport || detailPayload.assignedSalesSupport,
            // Keep complaint-derived Service Executive / Manager when create response omits them.
            ...mergeQuotationServiceRoleFields(detailPayload, {
              technicianId: form.technicianId,
              managerId: form.managerId,
              technicianName: form.technicianName || serviceExecutiveLabel,
              managerName: form.managerName || serviceManagerLabel,
              serviceExecutive: serviceExecutiveLabel,
              serviceManager: serviceManagerLabel,
            }),
          })
        : draftQuotation;
      const quotationId = createdQuotation.id ?? createdId ?? draftQuotation.refId;

      navigate(`/sales/quotations/quotation/${quotationId}`, {
        state: {
          quotation: createdQuotation,
          versionHistory: [createdQuotation],
          createdFromFlow: true,
        },
      });
    } catch (error) {
      const { message, fieldErrors } = extractCreateQuotationApiError(error);
      if (Object.keys(fieldErrors).length) {
        setErrors((current) => ({ ...current, ...fieldErrors }));
      }
      // Always surface the exact backend message when present — never substitute a hardcoded string.
      setSubmitError(message || Object.values(fieldErrors).filter(Boolean).join(" | "));
    } finally {
      setSubmitting(false);
    }
  }

  const isCreateDisabled =
    !form.refId.trim() ||
    !form.type ||
    (form.type === "SERVICE" && !String(form.complaintId || "").trim()) ||
    !form.customer ||
    !form.location ||
    !form.expiry ||
    submitting ||
    loadingComplaintDetail;

  function handleExcelFileInputChange(event) {
    const file = event.target.files?.[0] || null;
    const name = String(file?.name || "").toLowerCase();
    if (file && !(name.endsWith(".xlsx") || name.endsWith(".xls"))) {
      setExcelFile(null);
      setErrors((current) => ({
        ...current,
        excelFile: sq("excelUploadInvalidType"),
      }));
      event.target.value = "";
      return;
    }
    setExcelFile(file);
    setErrors((current) => ({ ...current, excelFile: "" }));
  }

  function clearExcelFile() {
    setExcelFile(null);
    setErrors((current) => ({ ...current, excelFile: "" }));
    if (excelFileInputRef.current) excelFileInputRef.current.value = "";
  }

  if (isMobileLayout) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
      <QuotationCreateMobileWizard
        step={mobileStep}
        onStepChange={setMobileStep}
        form={form}
        errors={errors}
        patchForm={patchForm}
        onQuotationTypeChange={handleQuotationTypeChange}
        isServiceType={isServiceType}
        showServiceRoles={showServiceRoles}
        serviceExecutiveLabel={serviceExecutiveLabel}
        serviceManagerLabel={serviceManagerLabel}
        complaintSelect={
          <ComplaintIdInfiniteSelect
            value={form.complaintId}
            onChange={handleComplaintIdChange}
            options={complaintOptions}
            loading={loadingComplaints}
            loadingMore={loadingMoreComplaints}
            hasMore={complaintHasMore}
            onLoadMore={loadMoreComplaints}
            disabled={loadingComplaints && complaintOptions.length === 0}
            hasError={Boolean(errors.complaintId)}
            placeholder={sq("selectComplaintId")}
          />
        }
        customerSelect={
          <CustomerInfiniteSelect
            value={form.customer}
            onChange={handleCustomerChange}
            options={customerOptions}
            loading={loadingCustomers}
            loadingMore={loadingMoreCustomers}
            hasMore={customerHasMore}
            onLoadMore={loadMoreCustomers}
            disabled={(loadingCustomers && customerOptions.length === 0) || loadingComplaintDetail}
            hasError={Boolean(errors.customer)}
            placeholder={loadingCustomers || loadingComplaintDetail ? "Loading..." : sq("selectCustomer")}
            addNewLabel={`+ ${sq("addNewCustomer")}`}
            addNewValue={ADD_ENTITY_OPTION}
          />
        }
        locationSelect={
          <FormSelect
            value={form.location}
            onChange={handleLocationChange}
            disabled={loadingLocations || loadingComplaintDetail}
            className={inputClass(Boolean(errors.location))}
            {...localizedFieldProps()}
          >
            <option value="">
              {!form.customer
                ? sq("selectCustomer")
                : loadingLocations || loadingComplaintDetail
                ? "Loading..."
                : locationOptions.length
                ? sq("selectLocation")
                : sq("noSavedLocations")}
            </option>
            {locationOptions.map((location) => (
              <option key={location.id} value={location.name}>
                {location.name}
              </option>
            ))}
            <option value={ADD_ENTITY_OPTION}>+ {sq("addNewLocation")}</option>
          </FormSelect>
        }
        contactSelect={
          <FormSelect
            value={form.contactName}
            onChange={handleContactChange}
            disabled={loadingContacts || loadingComplaintDetail}
            className={inputClass(Boolean(errors.contactName))}
            {...localizedFieldProps()}
          >
            <option value="">
              {!form.customer
                ? sq("selectCustomer")
                : loadingContacts || loadingComplaintDetail
                ? "Loading..."
                : contactOptions.length
                ? sq("selectContact")
                : sq("noSavedContacts")}
            </option>
            {contactOptions.map((contact) => (
              <option key={contact.id} value={contact.name}>
                {contact.name}
              </option>
            ))}
            <option value={ADD_ENTITY_OPTION}>+ {sq("addNewContact")}</option>
          </FormSelect>
        }
        salesExecutiveSelect={
          <FormSelect
            value={form.assignedSalesExecutive || ""}
            onChange={(event) => {
              const nextId = event.target.value;
              const nextName = resolveEmployeeDisplayName(nextId, salesExecutiveOptions);
              setForm((current) => ({
                ...current,
                assignedSalesExecutive: nextId,
                salesExecutive: nextName,
              }));
              setErrors((current) => ({ ...current, assignedSalesExecutive: "" }));
              setSubmitError("");
            }}
            onPointerDown={ensureSalesExecutiveOptions}
            onFocus={ensureSalesExecutiveOptions}
            className={inputClass(Boolean(errors.assignedSalesExecutive))}
            {...localizedFieldProps()}
          >
            <option value="">
              {loadingSalesExecutives && salesExecutiveOptions.length === 0
                ? "Loading..."
                : sq("selectSalesExecutive")}
            </option>
            {form.assignedSalesExecutive &&
              !salesExecutiveOptions.some(
                (option) => String(option.id) === String(form.assignedSalesExecutive)
              ) && (
                <option value={form.assignedSalesExecutive}>
                  {form.salesExecutive || form.assignedSalesExecutive}
                </option>
              )}
            {salesExecutiveOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </FormSelect>
        }
        salesSupportSelect={
          <FormSelect
            value={form.assignedSalesSupport || ""}
            onChange={(event) => {
              const nextId = event.target.value;
              const nextName = resolveEmployeeDisplayName(nextId, salesSupportOptions);
              setForm((current) => ({
                ...current,
                assignedSalesSupport: nextId,
                salesSupport: nextName,
              }));
              setErrors((current) => ({ ...current, assignedSalesSupport: "" }));
              setSubmitError("");
            }}
            onPointerDown={ensureSalesSupportOptions}
            onFocus={ensureSalesSupportOptions}
            className={inputClass(Boolean(errors.assignedSalesSupport))}
            {...localizedFieldProps()}
          >
            <option value="">
              {loadingSalesSupport && salesSupportOptions.length === 0
                ? "Loading..."
                : sq("selectSalesSupport")}
            </option>
            {form.assignedSalesSupport &&
              !salesSupportOptions.some(
                (option) => String(option.id) === String(form.assignedSalesSupport)
              ) && (
                <option value={form.assignedSalesSupport}>
                  {form.salesSupport || form.assignedSalesSupport}
                </option>
              )}
            {salesSupportOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </FormSelect>
        }
        submitError={submitError}
        apiError={customerApiError || employeeApiError || complaintApiError || complaintDetailError}
        onClearErrors={() => {
          setSubmitError("");
          setCustomerApiError("");
          setEmployeeApiError("");
          setComplaintApiError("");
          setComplaintDetailError("");
        }}
        onCancel={() => navigate("/sales/quotations/quotation")}
        onCreate={handleCreate}
        isCreateDisabled={isCreateDisabled}
        submitting={submitting}
        localizedFieldProps={localizedFieldProps}
      />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 px-0.5">
        <PageTitleRow back={<PageBackLink to="/sales/quotations/quotation" />}>
          <div className="min-w-0">
            <h2 className="text-[20px] font-semibold text-[#23324c]">{sq("createFormTitle")}</h2>
            <p className="mt-0.5 text-[13px] text-[#6f82a1]">{sq("createFormSubtitle")}</p>
          </div>
        </PageTitleRow>
        <StatusPill tone="draft" label={sq("statusDraft")} />
      </div>

      {(submitError || customerApiError || employeeApiError || complaintApiError || complaintDetailError) && (
        <ErrorState
          message={submitError || customerApiError || employeeApiError || complaintApiError || complaintDetailError}
          onRetry={() => {
            setSubmitError("");
            setCustomerApiError("");
            setEmployeeApiError("");
            setComplaintApiError("");
            setComplaintDetailError("");
          }}
        />
      )}

      <div className="grid gap-5 xl:grid-cols-2">
        <SectionShell title={sq("quotationDetails")}>
          <div className="grid gap-4 px-5 py-5 md:grid-cols-2 sm:px-6">
            <FormField label={sq("refId")} required error={errors.refId}>
              <input
                value={form.refId}
                onChange={(event) => patchForm("refId", event.target.value)}
                className={inputClass(Boolean(errors.refId))}
                placeholder={sq("refId")}
                {...localizedFieldProps()}
              />
            </FormField>

            <FormField label={sq("quotationType")} required error={errors.type}>
              <FormSelect
                value={form.type}
                onChange={(event) => handleQuotationTypeChange(event.target.value)}
                className={inputClass(Boolean(errors.type))}
                {...localizedFieldProps()}
              >
                <option value="">{sq("selectQuotationType")}</option>
                {SALES_TYPES.map((option) => (
                  <option key={option} value={option}>
                    {translateQuotationType(option)}
                  </option>
                ))}
              </FormSelect>
            </FormField>

            {isServiceType && (
              <FormField
                label={sq("complaintId")}
                required
                error={errors.complaintId}
                hint={loadingComplaintDetail ? "Loading complaint details..." : undefined}
              >
                <ComplaintIdInfiniteSelect
                  value={form.complaintId}
                  onChange={handleComplaintIdChange}
                  options={complaintOptions}
                  loading={loadingComplaints}
                  loadingMore={loadingMoreComplaints}
                  hasMore={complaintHasMore}
                  onLoadMore={loadMoreComplaints}
                  disabled={loadingComplaints && complaintOptions.length === 0}
                  hasError={Boolean(errors.complaintId)}
                  placeholder={sq("selectComplaintId")}
                />
              </FormField>
            )}

            <FormField label={sq("tenderDueDate")} error={errors.tenderDueDate}>
              <input
                type="date"
                value={form.tenderDueDate}
                onChange={(event) => patchForm("tenderDueDate", event.target.value)}
                className={inputClass(Boolean(errors.tenderDueDate))}
                {...localizedFieldProps()}
              />
            </FormField>

            <FormField label={sq("quotationExpiryDate")} required error={errors.expiry}>
              <input
                type="date"
                value={form.expiry}
                onChange={(event) => patchForm("expiry", event.target.value)}
                className={inputClass(Boolean(errors.expiry))}
                {...localizedFieldProps()}
              />
            </FormField>

            {showServiceRoles ? (
              <>
                <FormField label={sq("serviceExecutive")}>
                  <input
                    value={serviceExecutiveLabel}
                    readOnly
                    disabled
                    className={`${inputClass(false)} disabled:cursor-not-allowed disabled:bg-[#f8fbff] disabled:text-[#6f82a1]`}
                    placeholder="—"
                    {...localizedFieldProps()}
                  />
                </FormField>

                <FormField label={sq("serviceManager")}>
                  <input
                    value={serviceManagerLabel}
                    readOnly
                    disabled
                    className={`${inputClass(false)} disabled:cursor-not-allowed disabled:bg-[#f8fbff] disabled:text-[#6f82a1]`}
                    placeholder="—"
                    {...localizedFieldProps()}
                  />
                </FormField>
              </>
            ) : (
              <>
                <FormField label={sq("salesExecutive")} error={errors.assignedSalesExecutive}>
                  <FormSelect
                    value={form.assignedSalesExecutive || ""}
                    onChange={(event) => {
                      const nextId = event.target.value;
                      const nextName = resolveEmployeeDisplayName(nextId, salesExecutiveOptions);
                      setForm((current) => ({
                        ...current,
                        assignedSalesExecutive: nextId,
                        salesExecutive: nextName,
                      }));
                      setErrors((current) => ({ ...current, assignedSalesExecutive: "" }));
                      setSubmitError("");
                    }}
                    onPointerDown={ensureSalesExecutiveOptions}
                    onFocus={ensureSalesExecutiveOptions}
                    className={inputClass(Boolean(errors.assignedSalesExecutive))}
                    {...localizedFieldProps()}
                  >
                    <option value="">
                      {loadingSalesExecutives && salesExecutiveOptions.length === 0
                        ? "Loading..."
                        : sq("selectSalesExecutive")}
                    </option>
                    {form.assignedSalesExecutive &&
                      !salesExecutiveOptions.some(
                        (option) => String(option.id) === String(form.assignedSalesExecutive)
                      ) && (
                        <option value={form.assignedSalesExecutive}>
                          {form.salesExecutive || form.assignedSalesExecutive}
                        </option>
                      )}
                    {salesExecutiveOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </FormSelect>
                </FormField>

                <FormField label={sq("salesSupport")} error={errors.assignedSalesSupport}>
                  <FormSelect
                    value={form.assignedSalesSupport || ""}
                    onChange={(event) => {
                      const nextId = event.target.value;
                      const nextName = resolveEmployeeDisplayName(nextId, salesSupportOptions);
                      setForm((current) => ({
                        ...current,
                        assignedSalesSupport: nextId,
                        salesSupport: nextName,
                      }));
                      setErrors((current) => ({ ...current, assignedSalesSupport: "" }));
                      setSubmitError("");
                    }}
                    onPointerDown={ensureSalesSupportOptions}
                    onFocus={ensureSalesSupportOptions}
                    className={inputClass(Boolean(errors.assignedSalesSupport))}
                    {...localizedFieldProps()}
                  >
                    <option value="">
                      {loadingSalesSupport && salesSupportOptions.length === 0
                        ? "Loading..."
                        : sq("selectSalesSupport")}
                    </option>
                    {form.assignedSalesSupport &&
                      !salesSupportOptions.some(
                        (option) => String(option.id) === String(form.assignedSalesSupport)
                      ) && (
                        <option value={form.assignedSalesSupport}>
                          {form.salesSupport || form.assignedSalesSupport}
                        </option>
                      )}
                    {salesSupportOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </FormSelect>
                </FormField>
              </>
            )}

            <FormField
              label={sq("excelUpload")}
              error={errors.excelFile}
              fullWidth
            >
              <input
                ref={excelFileInputRef}
                type="file"
                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                className="sr-only"
                onChange={handleExcelFileInputChange}
              />
              <div
                role="button"
                tabIndex={0}
                onClick={() => excelFileInputRef.current?.click()}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    excelFileInputRef.current?.click();
                  }
                }}
                className={`flex min-h-[120px] cursor-pointer flex-col items-center justify-center gap-2.5 rounded-[12px] border border-dashed bg-[#fafcff] px-4 py-5 text-center transition hover:border-[#95addd] hover:bg-[#f5f8fd] ${
                  errors.excelFile ? "border-[#e4a5a0]" : "border-[#d1d9e8]"
                }`}
              >
                <div className="grid h-11 w-11 place-items-center rounded-[10px] border border-[#dde5f1] bg-white text-[#2944a8] shadow-[0_8px_18px_-16px_rgba(30,51,131,0.35)]">
                  <FileText className="h-5 w-5" />
                </div>
                {excelFile ? (
                  <div className="flex max-w-full flex-col items-center gap-2">
                    <p className="max-w-full truncate text-[13px] font-semibold text-[#2d394c]">
                      {excelFile.name}
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          excelFileInputRef.current?.click();
                        }}
                        className="inline-flex h-8 items-center rounded-[8px] border border-[#d0d9ea] bg-white px-3 text-[12px] font-semibold text-[#2944a8] transition hover:border-[#9eb3dc] hover:bg-[#f7faff]"
                      >
                        {sq("browse")}
                      </button>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          clearExcelFile();
                        }}
                        className="inline-flex h-8 items-center rounded-[8px] border border-[#d0d9ea] bg-white px-3 text-[12px] font-semibold text-[#6f82a1] transition hover:border-[#9eb3dc] hover:bg-[#f7faff] hover:text-[#2944a8]"
                      >
                        {sq("removeFile")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <p className="text-[13px] font-semibold text-[#2d394c]">{sq("noFileSelected")}</p>
                    <p className="text-[12px] text-[#8ea0bd]">{sq("excelUploadHint")}</p>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        excelFileInputRef.current?.click();
                      }}
                      className="mt-1 inline-flex h-8 items-center rounded-[8px] border border-[#d0d9ea] bg-white px-3 text-[12px] font-semibold text-[#2944a8] transition hover:border-[#9eb3dc] hover:bg-[#f7faff]"
                    >
                      {sq("browse")}
                    </button>
                  </div>
                )}
              </div>
            </FormField>
          </div>
        </SectionShell>

        <SectionShell title={sq("customerDetails")}>
          <div className="grid gap-4 px-5 py-5 md:grid-cols-2 sm:px-6">
            <FormField label={sq("customerName")} required error={errors.customer}>
              <CustomerInfiniteSelect
                value={form.customer}
                onChange={handleCustomerChange}
                options={customerOptions}
                loading={loadingCustomers}
                loadingMore={loadingMoreCustomers}
                hasMore={customerHasMore}
                onLoadMore={loadMoreCustomers}
                disabled={(loadingCustomers && customerOptions.length === 0) || loadingComplaintDetail}
                hasError={Boolean(errors.customer)}
                placeholder={loadingCustomers || loadingComplaintDetail ? "Loading..." : sq("selectCustomer")}
                addNewLabel={`+ ${sq("addNewCustomer")}`}
                addNewValue={ADD_ENTITY_OPTION}
              />
            </FormField>

            <FormField
              label={sq("addressOrLocation")}
              required
              error={errors.location}
            >
              <FormSelect
                value={form.location}
                onChange={handleLocationChange}
                disabled={loadingLocations || loadingComplaintDetail}
                className={inputClass(Boolean(errors.location))}
                {...localizedFieldProps()}
              >
                <option value="">
                  {!form.customer
                    ? sq("selectCustomer")
                    : loadingLocations || loadingComplaintDetail
                    ? "Loading..."
                    : locationOptions.length
                    ? sq("selectLocation")
                    : sq("noSavedLocations")}
                </option>
                {locationOptions.map((location) => (
                  <option key={location.id} value={location.name}>
                    {location.name}
                  </option>
                ))}
                <option value={ADD_ENTITY_OPTION}>+ {sq("addNewLocation")}</option>
              </FormSelect>
            </FormField>

            <FormField label={sq("pointOfContactName")} error={errors.contactName}>
              <FormSelect
                value={form.contactName}
                onChange={handleContactChange}
                disabled={loadingContacts || loadingComplaintDetail}
                className={inputClass(Boolean(errors.contactName))}
                {...localizedFieldProps()}
              >
                <option value="">
                  {!form.customer
                    ? sq("selectCustomer")
                    : loadingContacts || loadingComplaintDetail
                    ? "Loading..."
                    : contactOptions.length
                    ? sq("selectContact")
                    : sq("noSavedContacts")}
                </option>
                {contactOptions.map((contact) => (
                  <option key={contact.id} value={contact.name}>
                    {contact.name}
                  </option>
                ))}
                <option value={ADD_ENTITY_OPTION}>+ {sq("addNewContact")}</option>
              </FormSelect>
            </FormField>

            <FormField label={sq("phoneNumber")} error={errors.phone}>
              <input
                type="tel"
                value={form.phone}
                onChange={(event) => patchForm("phone", event.target.value)}
                disabled={!form.customer || loadingComplaintDetail}
                className={inputClass(Boolean(errors.phone))}
                placeholder="+91 98..."
                {...localizedFieldProps()}
              />
            </FormField>

            <FormField label={sq("email")} fullWidth error={errors.email}>
              <input
                type="email"
                value={form.email}
                onChange={(event) => patchForm("email", event.target.value)}
                disabled={!form.customer || loadingComplaintDetail}
                className={inputClass(Boolean(errors.email))}
                placeholder="contact@company.com"
                {...localizedFieldProps()}
              />
            </FormField>
          </div>
        </SectionShell>
      </div>

      <div className="mt-5 flex flex-wrap justify-end gap-3">
        <QuotationSecondaryButton icon={null} onClick={() => navigate("/sales/quotations/quotation")}>
          {i18n.t("formActions.cancel")}
        </QuotationSecondaryButton>
        <QuotationPrimaryButton icon={null} onClick={handleCreate} disabled={isCreateDisabled}>
          {submitting ? sq("creating") : sq("create")}
        </QuotationPrimaryButton>
      </div>
    </div>
  );
}



function QuotationCreatePage() {
  const isMobileLayout = useIsMobileLayout();
  const location = useLocation();
  // Preserve existing navigation-state handling for the creation URL.
  if (location.state?.quotation) return <QuotationDetailPage creationRoute />;

  // Keep the mobile wizard footer pinned below its scroll area.
  return (
    <AppLayout className={isMobileLayout ? "!flex !flex-col !overflow-hidden !py-4" : ""}>
      <div className={"mx-auto w-full max-w-[1280px] " + (isMobileLayout ? "flex min-h-0 flex-1 flex-col" : "")}>
        <CreateQuotationForm />
      </div>
    </AppLayout>
  );
}

export {
  QuotationCreatePage
};
